<script setup>
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAppearanceStore } from '@stores/appearance'
import { useMessages } from '@composables/use-messages'
import { useFavouriteToggle } from '@composables/use-favourite-toggle'
import copyToClipBoard from '@utils/copy-to-clipboard'
import { siteUrl } from '@/config/site'
import { shareOnFacebook, shareOnX } from '@utils/social-share'

const props = defineProps({
    mix: {
        type: Object,
        default: () => {},
    },
    hasViewMixOption: {
        type: Boolean,
        default: true,
    },
})

const route = useRoute()
const router = useRouter()
const appearanceStore = useAppearanceStore()
const getMessageText = useMessages()
const favourites = useFavouriteToggle()

const url = computed(() => {
    // Router 4 throws on a falsy required param, and this resolves during render.
    if (!props.mix.id) return ''

    const query = route.query.play === 'true' && route.name === 'mix' && String(props.mix.id) === route.params.id ? { play: 'true' } : {}

    return siteUrl(router.resolve({ name: 'mix', params: { id: props.mix.id }, query }).href)
})

const show = computed(() => props.mix.show)
const artist = computed(() => props.mix.artist)

const isLiked = computed(() => Boolean(props.mix.id) && favourites.isFavourite({ type: 'mixes', id: props.mix.id }))
const isFollowingShow = computed(() => Boolean(show.value && show.value.id) && favourites.isFavourite({ type: 'shows', id: show.value.id }))
const isFollowingArtist = computed(() => Boolean(artist.value && artist.value.id) && favourites.isFavourite({ type: 'artists', id: artist.value.id }))

const header = computed(() => ({
    image: props.mix.episode,
    title: (artist.value && artist.value.title) || props.mix.title,
    subtitle: show.value && show.value.title,
}))

function copyLinkToClipBoard() {
    copyToClipBoard(url.value)
    appearanceStore.handleNotification({
        message: getMessageText('LINK_COPIED_CLIPBOARD'),
        type: 'success',
    })
}

// A row whose entity has no id is hidden: the View row's router-link would throw resolving
// a missing param, and master's favourite buttons went inert (pointer-events: none) for it.
const items = computed(() => [
    { id: 'view', label: 'View Mix', to: { name: 'mix', params: { id: props.mix.id } }, hidden: !props.hasViewMixOption || !props.mix.id },
    { divider: true },
    {
        id: 'like',
        label: isLiked.value ? 'Unlike' : 'Like',
        hidden: !props.mix.id,
        action: () => favourites.toggle({ type: 'mixes', id: props.mix.id, addedKey: 'FAV_MIX_ADDED', removedKey: 'FAV_MIX_REMOVED' }),
    },
    {
        id: 'follow-show',
        label: isFollowingShow.value ? 'Following' : 'Follow Show',
        hidden: !show.value || !show.value.id,
        action: () => favourites.toggle({ type: 'shows', id: show.value.id, addedKey: 'FAV_SHOW_ADDED', removedKey: 'FAV_SHOW_REMOVED' }),
    },
    {
        id: 'follow-artist',
        label: isFollowingArtist.value ? 'Following' : 'Follow Artist',
        hidden: !artist.value || !artist.value.id,
        action: () => favourites.toggle({ type: 'artists', id: artist.value.id, addedKey: 'FAV_ARTIST_ADDED', removedKey: 'FAV_ARTIST_REMOVED' }),
    },
    { divider: true },
    // Interim (R8): today's three share actions as plain rows. The single `Share ›` row
    // lands with the Share surface ticket 1218209213218817.
    { id: 'share-facebook', label: 'Facebook', action: () => shareOnFacebook(url.value) },
    { id: 'share-x', label: 'X', action: () => shareOnX(url.value, `${props.mix.title} on @friskyradio #feelinfrisky`) },
    { id: 'copy-link', label: 'Copy Mix Link', action: copyLinkToClipBoard },
])
</script>

<template>
    <BaseContextMenu
        class="mix-options"
        :items="items"
        :header="header"
        trigger-label="Mix options"
    >
        <template
            v-if="$slots.trigger"
            #trigger="slotProps"
        >
            <slot
                name="trigger"
                v-bind="slotProps"
            />
        </template>
    </BaseContextMenu>
</template>
